// Answers the phone already has, waiting for the request that would fetch them (OFFLINE_SYNC_SPEED_PLAN_2026-10-03.md, Rev. 4).
//
// The offline sync asks the server for the answers of several units in ONE request (the "offline bundle") and puts every part
// here, keyed by the path the normal code is about to request. The HTTP interceptor then answers that GET from here instead of the
// network, so the normal per-unit code (cache keys, writers, row bases, the "saved fresh" check) runs exactly as before; only the
// transport is different.
//
// Safe by construction:
//  - an entry is used ONCE and only for an exact path + query match, anything else goes to the network as usual;
//  - an entry lives TTL_MS only (a stale entry is dropped, never used);
//  - the store is empty unless a bundle was just loaded, so on the website and outside a sync it does nothing;
//  - it is emptied at once when the session ends (same signal that stops the sync).

import { onOfflineSyncCancel } from './offline-sync-control';

const TTL_MS = 30_000;
const MAX_ENTRIES = 400;

const entries = new Map<string, { body: unknown; at: number }>();
let hitListener: (() => void) | null = null;

// Proof that a request was REALLY answered (by the server, or by a bundle that came from the server): the cache helpers answer a failed
// request with the OLD saved copy, which looks exactly like success to the caller. The HTTP layer notes every real answer here while a sync
// run is going (tracking on), and the sync asks "did the server answer this path during this unit's run?". Not kept outside a run.
const answered = new Map<string, number>();
const MAX_ANSWERED = 800;
let tracking = false;

export function setAnswerTracking(on: boolean): void {
  tracking = on;
  if (!on) answered.clear();
}

export function noteAnswered(url: string): void {
  if (!tracking) return;
  const key = normalizePrefillUrl(url);
  answered.delete(key);
  answered.set(key, Date.now());
  if (answered.size > MAX_ANSWERED) {
    const oldest = answered.keys().next().value;
    if (oldest !== undefined) answered.delete(oldest);
  }
}

export function wasAnsweredSince(url: string, since: number): boolean {
  const at = answered.get(normalizePrefillUrl(url));
  return at !== undefined && at >= since;
}

/** "https://api.x.com/api/v1/a?b=1#h" and "/api/v1/a?b=1" are the same request path. */
export function normalizePrefillUrl(url: string): string {
  const withoutHash = url.split('#')[0];
  return withoutHash.replace(/^[a-z][a-z0-9+.-]*:\/\/[^/]+/i, '');
}

export function putPrefilled(url: string, body: unknown): void {
  if (!url || body === undefined) return;
  if (entries.size >= MAX_ENTRIES) {
    const oldest = entries.keys().next().value;
    if (oldest !== undefined) entries.delete(oldest);
  }
  entries.set(normalizePrefillUrl(url), { body, at: Date.now() });
}

/** Takes (and removes) the waiting answer for this request, if there is a fresh one. */
export function takePrefilled(url: string): { hit: boolean; body?: unknown } {
  if (entries.size === 0) return { hit: false };
  const key = normalizePrefillUrl(url);
  const entry = entries.get(key);
  if (!entry) return { hit: false };
  entries.delete(key);
  if (Date.now() - entry.at > TTL_MS) return { hit: false };
  try { hitListener?.(); } catch { /* the listener only does bookkeeping */ }
  noteAnswered(url); // it came from a real server answer (the bundle)
  return { hit: true, body: entry.body };
}

export function clearPrefilled(): void {
  entries.clear();
}

export function prefilledCount(): number {
  return entries.size;
}

/** Called each time a request is answered from the store (the request gate gives back the request it had counted). */
export function setPrefillHitListener(listener: (() => void) | null): void {
  hitListener = listener;
}

// The session ended or the identity is being wiped: nothing waiting may be used by whoever comes next.
onOfflineSyncCancel(() => { clearPrefilled(); setAnswerTracking(false); });
